import { PriceType } from '@prisma/client'
import { Status } from 'components/common/Status/Status'
import { Group } from 'domain/models/Group/EntityGroup'
import { VariantInventory } from 'domain/models/Inventory/VariantInventory'

interface GetRaceStatusDefaultProps {
  isArchived: boolean
  isActive: boolean
  raceWaitingListConfiguration: {
    enabled: boolean
  } | null
  registrationOpeningDate: Date | null
  registrationClosingDate: Date | null
  editionRegistrationOpeningDate: Date | null
  editionRegistrationClosingDate: Date | null
  raceStockInventory: VariantInventory | undefined | null
  date?: Date
  // Group can bypass default registration date before and after
  group: Pick<Group, 'groupRegistrationStatus'> | null
}

interface GetRaceStatusWithProjected extends GetRaceStatusDefaultProps {
  withProjected: true
  minTeamSize: number | null
  raceSegmentsLength: number
  priceType: PriceType
}

interface GetRaceStatusWithoutProjectedProps extends GetRaceStatusDefaultProps {
  withProjected?: false
}

interface GetRaceStatusWithoutProjectedProps
  extends GetRaceStatusDefaultProps {}

export type GetRaceStatusProps =
  | GetRaceStatusWithProjected
  | GetRaceStatusWithoutProjectedProps

export const getRaceStatus = ({
  isArchived,
  isActive,
  raceWaitingListConfiguration,
  registrationOpeningDate,
  registrationClosingDate,
  editionRegistrationOpeningDate,
  editionRegistrationClosingDate,
  raceStockInventory,
  group,
  date = new Date(),
  ...props
}: GetRaceStatusProps): Status => {
  const raceRegistrationOpeningDate =
    registrationOpeningDate ?? editionRegistrationOpeningDate
  const raceRegistrationClosingDate =
    registrationClosingDate ?? editionRegistrationClosingDate
  const groupIsOpen =
    group && isActive && group.groupRegistrationStatus === 'open'
  const isOpen =
    isActive &&
    raceRegistrationOpeningDate &&
    raceRegistrationClosingDate &&
    date >= raceRegistrationOpeningDate &&
    date <= raceRegistrationClosingDate

  const isWaitingListEnabled = raceWaitingListConfiguration?.enabled ?? false

  if (isArchived) {
    return Status.ARCHIVED
  }

  if (!groupIsOpen && isWaitingListEnabled) {
    return Status.WAITING_LIST
  }

  // if the race is out of stock, it prevails over the registration dates
  if (raceStockInventory) {
    if (raceStockInventory.reserved >= raceStockInventory.stockedQuantity) {
      return Status.OUT_OF_STOCK
    }
    if (
      props.withProjected === true &&
      props.minTeamSize &&
      props.raceSegmentsLength > 0
    ) {
      const projectedQuantity =
        props.priceType === 'PER_TEAM'
          ? 1
          : props.minTeamSize * props.raceSegmentsLength
      if (
        raceStockInventory.reserved + projectedQuantity >
        raceStockInventory.stockedQuantity
      ) {
        return Status.OUT_OF_STOCK
      }
    }
  }

  if (!isOpen && !groupIsOpen) {
    return Status.INACTIVE
  }

  if (!raceStockInventory) {
    return Status.ONLINE
  }

  return Status.ONLINE
}
